// apps/smart-shikka-web/src/pages/Chat.tsx
// PR #767 — Web binding of the shared Smart Shikka product shell.
//
// Chat, Code, Shikka Design and Cover are four modes of ONE shell, rendered with
// the same primitives Desktop consumes. Only canonical logical product profiles
// are selectable here: no provider/model identity is sent or shown, and the
// permission strip only requests grants the server re-evaluates.
//
// CORE-14 modularization (move-only): API/CSRF/message-mapping helpers live in
// ./chat/chatApi.ts and the voice runtime lives in ./chat/useVoiceSession.ts.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth.js";
import { AppNav, type NavSession } from "../components/AppNav.js";
import {
  COVER_PRESETS,
  ChatCanvas,
  ChatComposer,
  DesignModeSurface,
  ForegroundWakeControl,
  ModeSelector,
  PermissionStrip,
  ProductPreferenceControls,
  ProductShell,
  VoicePlayer,
  VoiceRecorder,
  consumeOncePermissionCategories,
  createDefaultPermissionCategories,
  createProductDesignService,
  ApiClient,
  COOKIE_SESSION_TOKEN,
  VOICE_PREFERENCE_SETTING_KEY,
  createCanonicalSettingsService,
  createVoiceService,
  createWorkspaceCeoService,
  createWorkspaceCodeService,
  productText,
  safeErrorCode,
  safeErrorText,
  errorToCode,
  resolveProductModeRuntime,
  useBrandColors,
  type DesignExportResult,
  type PermissionCategoryId,
  type PermissionCategoryState,
  type PermissionGrantRequestId,
  type ProductChatMessage,
  type ServerPermissionState,
  type SmartShikkaEffortLevel,
  type SmartShikkaLogicalProfileId,
  type SmartShikkaProductMode,
  type CeoApprovalRequest,
  type CeoInputAttachment,
} from "@smart-shikka/react";
import { useLocale, useTheme, SHIKKA_LOGO } from "../ui.js";
import { WebControlContextPane } from "../panels/WebControlContextPane.js";
import {
  BASE,
  MAX_ATTACH_BYTES,
  buildHeaders,
  createConversationSession,
  fetchConversationList,
  fetchConversationMessages,
  postAsk,
  type ChatAttachment,
  type Session,
} from "./chat/chatApi.js";
import { useVoiceSession } from "./chat/useVoiceSession.js";
import { ApprovalConsumeCard, type ApprovalDecisionState } from "./chat/ApprovalCard.js";
import { SupportActionsCard, isSupportTurn, isExplicitHumanRequest } from "./chat/SupportActions.js";
import { supportApi } from "../api.js";

export interface ChatPageProps {
  initialRoute?: "chat" | "projects" | "files" | "automation" | "apps" | "skills" | "settings";
}

export function ChatPage({ initialRoute = "chat" }: ChatPageProps) {
  const { token, isPrivileged, account } = useAuth();
  const C = useBrandColors();
  const { locale, setLocale, tr } = useLocale();
  const { resolved: theme, toggle: toggleTheme } = useTheme();

  const [sessions, setSessions] = useState<Session[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ProductChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<"ready" | "error">("ready");
  // §5 — Smart Shikka provider badge: server truth from /health, never a static claim.
  const [coreBadge, setCoreBadge] = useState<"live" | "connected" | "offline">("offline");
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(`${BASE()}/health`, { headers: buildHeaders(token), credentials: "include" });
        if (!r.ok) { if (!cancelled) setCoreBadge("offline"); return; }
        const body = (await r.json()) as { provider?: { credentialStatus?: string; healthStatus?: string; enabled?: boolean } };
        const p = body.provider;
        const next =
          p?.healthStatus === "ready"
            ? "live"
            : p?.credentialStatus === "configured" || p?.enabled === true
              ? "connected"
              : "offline";
        if (!cancelled) setCoreBadge(next);
      } catch {
        if (!cancelled) setCoreBadge("offline");
      }
    };
    void poll();
    const t = setInterval(() => void poll(), 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [token]);
  // "connection" = transport/network failure; "request" = server answered
  // with a classified error (validation, policy, capability) — not a
  // connectivity problem, so the pill must not claim कनेक्शन त्रुटि.
  const [statusKind, setStatusKind] = useState<"connection" | "request">("connection");
  const [attachment, setAttachment] = useState<ChatAttachment | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [leftPanelOpen, setLeftPanelOpen] = useState(() => {
    try {
      return window.localStorage.getItem("ss.webLeftPanelOpen.v1") !== "0";
    } catch {
      return true;
    }
  });
  const [controlPaneOpen, setControlPaneOpen] = useState(() => {
    try {
      return window.localStorage.getItem("ss.webControlPaneOpen.v1") !== "0";
    } catch {
      return true;
    }
  });

  const [mode, setMode] = useState<SmartShikkaProductMode>("chat");
  const [logicalProfile, setLogicalProfile] = useState<SmartShikkaLogicalProfileId>("auto");
  const [effort, setEffort] = useState<SmartShikkaEffortLevel>("medium");
  const [creativity, setCreativity] = useState(0.7);
  const [coverPresetId, setCoverPresetId] = useState(COVER_PRESETS[0]!.id);

  const [categories, setCategories] = useState<PermissionCategoryState[]>(() => createDefaultPermissionCategories());
  const [serverPermissionState, setServerPermissionState] = useState<ServerPermissionState | undefined>(undefined);
  const [approvalPending, setApprovalPending] = useState(false);
  // §Q — one pending approval card inside the normal chat. `sessionId` is the
  // client-chosen session id sent on the originating turn; the server binds
  // the approve consume to the same actor + session + action digest.
  const [approvalCard, setApprovalCard] = useState<
    | {
        request: CeoApprovalRequest;
        input: string;
        attachments?: readonly CeoInputAttachment[];
        sessionId: string;
        state: ApprovalDecisionState;
        errorCode?: string;
      }
    | null
  >(null);

  const [projectId, setProjectId] = useState<string | undefined>(undefined);
  const [designArtifact, setDesignArtifact] = useState<DesignExportResult | undefined>(undefined);
  const [designError, setDesignError] = useState<string | undefined>(undefined);
  const [imageGenerationStatus, setImageGenerationStatus] = useState<
    "not_requested" | "not_configured" | undefined
  >(undefined);

  const headers = useCallback((): HeadersInit => buildHeaders(token), [token]);

  const designService = useMemo(() => createProductDesignService({ baseUrl: BASE(), token: token ?? undefined }), [token]);
  const ceoService = useMemo(
    () => createWorkspaceCeoService({ baseUrl: BASE(), getToken: () => token ?? undefined }),
    [token],
  );
  const voiceService = useMemo(
    () =>
      createVoiceService(
        new ApiClient({
          baseUrl: BASE(),
          ...(token ? { authToken: token } : {}),
        }),
      ),
    [token],
  );
  // UX-05 — persisted preferred voice (canonical `voice` namespace) feeds the
  // synthesized spoken reply; absent preference keeps the server default.
  const [preferredVoiceId, setPreferredVoiceId] = useState<string | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    const svc = createCanonicalSettingsService({ token: token ?? COOKIE_SESSION_TOKEN });
    void svc
      .list("voice")
      .then((rows) => {
        if (cancelled) return;
        const pref = rows.find((row) => row.settingKey === VOICE_PREFERENCE_SETTING_KEY)?.settingValue?.voiceId;
        if (typeof pref === "string" && pref.length > 0) setPreferredVoiceId(pref);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [token]);

  const codeService = useMemo(
    () => createWorkspaceCodeService({
      baseUrl: BASE(),
      getToken: () => token ?? undefined,
      getProjectContext: () => ({ projectId, projectName: projectId }),
      surface: "web",
    }),
    [token, projectId],
  );

  const suggestions = useMemo(() => {
    if (mode === "code") {
      return locale === "hi"
        ? ["इस repo का audit करो", "एक React component बनाओ", "यह bug ठीक करो", "API route review करो"]
        : ["Audit this repo", "Build a React component", "Fix this bug", "Review an API route"];
    }
    if (mode === "design" || mode === "cover") {
      return locale === "hi"
        ? ["सफ़ेद-सुनहरा poster बनाओ", "Launch banner बनाओ", "YouTube thumbnail बनाओ", "Festival post बनाओ"]
        : ["Create a cream and gold poster", "Design a launch banner", "Make a YouTube thumbnail", "Make a festival post"];
    }
    return locale === "hi"
      ? ["आज का प्लान बनाओ", "यह दस्तावेज़ समझाओ", "SEO content लिखो", "कोई भी प्रश्न पूछें"]
      : ["Plan my day", "Explain this document", "Write SEO content", "Ask anything"];
  }, [locale, mode]);

  useEffect(() => {
    if (!token) return;
    let pendingOpen: string | null = null;
    try {
      pendingOpen = window.sessionStorage.getItem("ss.openConversationId");
      window.sessionStorage.removeItem("ss.openConversationId");
    } catch { /* storage unavailable */ }
    fetchConversationList(headers(), tr("anonymousChat"))
      .then((list) => {
        setSessions(list);
        if (pendingOpen && list.some((s) => s.id === pendingOpen)) setActiveId(pendingOpen);
      })
      .catch(() => setStatus("error"));
  }, [token, headers, tr]);

  useEffect(() => {
    try {
      window.localStorage.setItem("ss.webLeftPanelOpen.v1", leftPanelOpen ? "1" : "0");
    } catch {}
  }, [leftPanelOpen]);

  useEffect(() => {
    try {
      window.localStorage.setItem("ss.webControlPaneOpen.v1", controlPaneOpen ? "1" : "0");
    } catch {}
  }, [controlPaneOpen]);

  useEffect(() => {
    if (!token) return;
    fetch(`${BASE()}/api/projects`, { headers: headers(), credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const list = Array.isArray(d?.projects) ? (d.projects as Record<string, unknown>[]) : [];
        const first = list.find((p) => typeof p.projectId === "string");
        setProjectId(first ? String(first.projectId) : undefined);
      })
      .catch(() => setProjectId(undefined));
  }, [token, headers]);

  useEffect(() => {
    setApprovalCard(null);
    if (!activeId || !token) { setMessages([]); return; }
    fetchConversationMessages(headers(), activeId)
      .then(setMessages)
      .catch(() => {});
  }, [activeId, token, headers]);

  const newChat = useCallback(async () => {
    if (!token) return;
    const session = await createConversationSession(headers(), tr("newChat"));
    if (session) {
      setSessions((prev) => [session, ...prev]);
      setActiveId(session.id);
    }
    setMessages([]);
    setDesignArtifact(undefined);
    setDesignError(undefined);
  }, [token, headers, tr]);

  /**
   * Scopes the user asked the server to consider. The server intersects these with
   * the authenticated boundary and re-evaluates every request, so listing a scope
   * here can never widen authority.
   */
  const requestedScopes = useMemo(
    () => categories.filter((c) => c.requested !== "ask_every_time").flatMap((c) => [...c.scopes]),
    [categories],
  );

  const appendMessage = useCallback((role: ProductChatMessage["role"], content: string) => {
    setMessages((prev) => [...prev, { id: `${role}-${Date.now()}-${prev.length}`, role, content }]);
  }, []);

  // CORE-HELP-02: post-answer support actions — only on support-ish turns (§6).
  const [supportTurn, setSupportTurn] = useState<{
    query: string;
    answer: string;
    conversationId?: string | undefined;
    ticketNumber?: string | undefined;
    busy?: boolean | undefined;
  } | null>(null);

  /**
   * §Q — consume/deny the pending approval via the canonical /api/ceo/approve
   * route. Approve resubmits the digest-bound input; Deny revokes. The card
   * keeps one decision per approval — a consumed/denied/expired card can
   * never be re-submitted (the server also rejects replays).
   */
  const resolveApprovalDecision = useCallback(async (granted: boolean) => {
    const card = approvalCard;
    if (!card || card.state !== "pending") return;
    // The server requires the action digest on a granted consume — if the turn
    // response did not carry one the approval cannot execute, so fail closed.
    if (granted && !card.request.actionDigest) {
      setApprovalCard({ ...card, state: "failed", errorCode: "approval_digest_missing" });
      setApprovalPending(false);
      return;
    }
    setApprovalCard({ ...card, state: "submitting" });
    const result = await ceoService.approve({
      approvalId: card.request.approvalId,
      granted,
      sessionId: card.sessionId,
      ...(granted
        ? {
            actionDigest: card.request.actionDigest,
            input: card.input,
            ...(card.attachments !== undefined ? { attachments: card.attachments } : {}),
          }
        : {}),
    });
    if (!granted) {
      const denyCode = result.errorCode;
      const deniedState: ApprovalDecisionState =
        denyCode === "approval_replayed"
          ? "replayed"
          : denyCode === "approval_expired"
            ? "expired"
            : "denied";
      setApprovalCard({
        ...card,
        state: deniedState,
        ...(denyCode !== undefined ? { errorCode: denyCode } : {}),
      });
      setApprovalPending(false);
      appendMessage("assistant", result.answer || productText("approvalDeniedDone", locale));
      return;
    }
    if (result.ok) {
      setApprovalCard({ ...card, state: "consumed" });
      setApprovalPending(false);
      if (result.answer) appendMessage("assistant", result.answer);
      return;
    }
    const code = result.errorCode;
    if (code === "approval_replayed") {
      setApprovalCard({ ...card, state: "replayed", errorCode: code });
    } else if (code === "approval_expired") {
      setApprovalCard({ ...card, state: "expired", errorCode: code });
    } else if (code === "ceo_approve_error" || code === "unauthenticated") {
      // Transport/auth failure — the approval may still be pending server-side,
      // so keep the card actionable instead of burning the single consume.
      setApprovalCard({ ...card, state: "pending", errorCode: code });
    } else {
      setApprovalCard({ ...card, state: "failed", ...(code !== undefined ? { errorCode: code } : {}) });
    }
    setApprovalPending(false);
    appendMessage(
      "assistant",
      result.answer || safeErrorText(safeErrorCode(code), locale).title,
    );
  }, [approvalCard, appendMessage, ceoService, locale]);

  /**
   * One user-facing chat path: every normal turn goes through `/api/ask`.
   * The server resolves the authenticated role itself and delegates
   * super-admin / super-sub-admin turns to the canonical Shikka CEO
   * authority — the client never selects the authority. A delegated turn
   * answers with the canonical CEO response shape (`ok` boolean), so the
   * approval card / safe-error handling below only engages for responses
   * the server actually delegated.
   */
  const askServer = useCallback(async (query: string, sessionId: string | undefined, sentAttachment: ChatAttachment | null, conversationId?: string, idempotencyKey?: string) => {
    // §Q — the server only issues approvals for session-bound turns; bind the
    // stable conversation-derived id so an approval-required turn carries it.
    const turnSessionId = sessionId ?? (conversationId ? `web-${conversationId}` : undefined);
    const body: Record<string, unknown> = {
      query,
      sessionId: turnSessionId,
      logicalProfile,
      productMode: mode,
      effort,
      grantedScopes: requestedScopes,
      temperature: creativity,
      surface: "web",
      locale,
      // Attachments travel on the same route; the server either delegates
      // them to the canonical CEO attachment runtime (privileged) or reports
      // the truthful attachmentState (standard).
      ...(sentAttachment ? { attachments: [sentAttachment] } : {}),
      // CORE-08 — bind to the same canonical conversation ledger.
      ...(conversationId ? { conversationId } : {}),
      ...(idempotencyKey ? { idempotencyKey } : {}),
    };
    const d = await postAsk(headers(), body);
    if (!d) { setStatus("error"); return ""; }

    if (typeof d.ok === "boolean") {
      // Delegated canonical CEO response.
      const errorCode = typeof d.errorCode === "string" ? d.errorCode : undefined;
      setStatus(d.ok ? "ready" : "error");
      setStatusKind(errorCode !== undefined && errorCode !== "ceo_turn_error" ? "request" : "connection");
      setApprovalPending(d.requiresApproval === true);
      const approvalRequest = d.approvalRequest as CeoApprovalRequest | undefined;
      if (d.requiresApproval === true && approvalRequest?.approvalId) {
        setApprovalCard({
          request: approvalRequest,
          input: query,
          ...(sentAttachment ? { attachments: [sentAttachment] } : {}),
          // Consume must resubmit the same session id the turn carried — the
          // server binds pending approval to actor+session+digest.
          sessionId: approvalRequest.sessionId ?? turnSessionId ?? "",
          state: "pending",
        });
      } else {
        setApprovalCard(null);
      }
      const answer = String(d.answer ?? "") || (errorCode !== undefined
        ? safeErrorText(safeErrorCode(errorCode), locale).title
        : "ceo_turn_failed");
      appendMessage("assistant", answer);
      return String(d.answer ?? "");
    }

    setStatus("ready");
    const attachmentState = d.attachmentState as Record<string, unknown> | undefined;
    if (sentAttachment && attachmentState && attachmentState.attachmentRuntimeSupported === false && Number(attachmentState.attachmentsReceived) > 0) {
      appendMessage("assistant", productText("attachmentNeedsCeoRoute", locale));
      if (!query.trim()) return "";
    }
    const permission = d.permissionState as ServerPermissionState | undefined;
    if (permission) setServerPermissionState(permission);
    const planning = d.agentPlanning as Record<string, unknown> | undefined;
    setApprovalPending(planning?.requiresConfirmation === true || planning?.allowed === false);
    const answer = String(d.answer ?? d.reply ?? "");
    if (answer) appendMessage("assistant", answer);
    return answer;
  }, [appendMessage, creativity, effort, headers, locale, logicalProfile, mode, requestedScopes]);

  const {
    listening,
    micError,
    setMicError,
    voicePanelOpen,
    voiceConsent,
    voiceBusy,
    setVoiceActivity,
    voiceAudioSrc,
    setForegroundWakeActive,
    voiceRecordingActive,
    enableVoice,
    cancelVoiceInteraction,
    handleVoiceRecordingState,
    handleVoiceRecordingRequested,
    handleVoiceRecording,
    handleForegroundWakeWord,
    handleForegroundWakeState,
    handleForegroundWakeCommandDetected,
    toggleVoice,
  } = useVoiceSession({
    isPrivileged,
    token,
    accountRole: account?.role,
    locale,
    tr,
    activeId,
    voiceService,
    ceoService,
    preferredVoiceId,
    appendMessage,
    setInput,
    setStatus,
    setApprovalPending,
    onApprovalRequest: (request, input, sessionId) => {
      setApprovalCard({ request, input, sessionId, state: "pending" });
    },
  });

  const runCodingTask = useCallback(async (description: string, conversationId?: string, idempotencyKey?: string) => {
    if (!projectId) {
      appendMessage("assistant", productText("codeProjectRequired", locale));
      return;
    }
    try {
      const result = await codeService.generateTask({
        description,
        files: [],
        logicalProfile,
        productMode: "code",
        effort,
        ...(conversationId ? { conversationId } : {}),
        ...(idempotencyKey ? { idempotencyKey } : {}),
      });
      if (!result.success) {
        appendMessage(
          "assistant",
          safeErrorText(safeErrorCode(result.errorCode), locale).title,
        );
        return;
      }
      const changes = result.changes ?? [];
      if (changes.length === 0) {
        appendMessage("assistant", productText("codeNoChanges", locale));
        return;
      }
      const lines = changes.map((change) => `${change.operation} ${change.path}`);
      appendMessage(
        "assistant",
        `${productText("codePlanReady", locale)} (${result.status ?? "plan_only"}):\n${lines.join("\n")}`,
      );
    } catch (err) {
      appendMessage("assistant", safeErrorText(errorToCode(err), locale).title);
    }
  }, [appendMessage, codeService, effort, locale, logicalProfile, projectId]);

  const runDesignTask = useCallback(async (prompt: string, conversationId?: string, idempotencyKey?: string) => {
    setDesignError(undefined);
    setImageGenerationStatus(undefined);
    const preset = COVER_PRESETS.find((p) => p.id === coverPresetId) ?? COVER_PRESETS[0]!;
    const dimensions = mode === "cover"
      ? { width: preset.width, height: preset.height, unit: preset.unit, dpi: preset.dpi }
      : { width: 1080, height: 1080, unit: "px" as const, dpi: 72 };
    const result = designArtifact
      ? await designService.editDesign({
          projectId: designArtifact.projectId,
          prompt,
          format: "png",
          ...(conversationId ? { conversationId } : {}),
          ...(idempotencyKey ? { idempotencyKey } : {}),
        })
      : await designService.generateDesign({
          prompt,
          name: prompt.slice(0, 60),
          projectScope: projectId,
          canvas: {
            width: dimensions.width,
            height: dimensions.height,
            dpi: dimensions.dpi,
          },
          ...(conversationId ? { conversationId } : {}),
          ...(idempotencyKey ? { idempotencyKey } : {}),
        });
    if (!result.ok) {
      setDesignError(safeErrorText(safeErrorCode(result.errorCode), locale).title);
      return;
    }
    setDesignArtifact(result.data);
    setImageGenerationStatus(result.data.imageGeneration.status);
  }, [coverPresetId, designArtifact, designService, mode, projectId]);

  // §2/§6 — one canonical ticket per unresolved question; idempotency key
  // makes repeat presses return the same ticket instead of duplicating.
  const escalateChatToHuman = useCallback(async (turn: {
    query: string; answer: string; conversationId?: string | undefined;
  }) => {
    if (!token) return;
    setSupportTurn({ ...turn, busy: true });
    try {
      const r = await supportApi.createTicket(token, {
        subject: turn.query.slice(0, 120),
        body:
          turn.query +
          (turn.answer ? `\n\n— Smart Shikka का उत्तर —\n${turn.answer.slice(0, 2000)}` : ""),
        explicitHumanRequest: true,
        ...(turn.conversationId !== undefined ? { sourceConversationId: turn.conversationId } : {}),
        context: {
          route: "#chat",
          ...(turn.conversationId !== undefined ? { conversationId: turn.conversationId } : {}),
        },
        idempotencyKey: `chat-${turn.conversationId ?? "anon"}-${turn.query.slice(0, 64)}`,
      });
      setSupportTurn({ ...turn, ticketNumber: r.ticket.ticketNumber });
    } catch {
      setSupportTurn({ ...turn });
    }
  }, [token]);

  const send = useCallback(async (raw: string) => {
    const content = raw.trim();
    if (!content && !attachment) return;
    const text = content || (attachment ? `[${attachment.name}]` : "");
    const sentAttachment = attachment;
    setInput("");
    setAttachment(null);
    setAttachError(null);
    setApprovalCard(null);

    let convId = activeId ?? undefined;
    if (!convId && token) {
      const session = await createConversationSession(headers(), text.slice(0, 50));
      if (session) {
        convId = session.id;
        setSessions((prev) => [session, ...prev]);
        setActiveId(session.id);
      }
    }

    appendMessage("user", text);
    setSupportTurn(null);
    setBusy(true);
    // CORE-08 — one key per user send; safe resend of the same request replays
    // the recorded turn instead of writing a duplicate durable message.
    const turnKey = crypto.randomUUID();
    // One prompt drives exactly one canonical runtime for the active mode.
    const runtime = resolveProductModeRuntime(mode);
    if (runtime === "coding_workflow") {
      await runCodingTask(text, convId, turnKey);
    } else if (runtime === "design_studio") {
      await runDesignTask(text, convId, turnKey);
    } else {
      // conversationId != sessionId: the ledger-bound turn uses the canonical
      // conversation id; the legacy server session stays its own id space.
      const answer = await askServer(text, undefined, sentAttachment, convId, turnKey);
      // CORE-HELP-02 §6: support-ish turns offer solved / human-help actions.
      // §2 — an explicit human request escalates straight to a ticket; the
      // user must not be pushed through another AI loop first.
      if (isSupportTurn(text) || isExplicitHumanRequest(text)) {
        const turn = {
          query: text,
          answer: answer ?? "",
          ...(convId !== undefined ? { conversationId: convId } : {}),
        };
        setSupportTurn(turn);
        if (isExplicitHumanRequest(text)) {
          void escalateChatToHuman(turn);
        }
      }
    }
    setCategories((prev) => consumeOncePermissionCategories(prev));
    setBusy(false);
  }, [activeId, appendMessage, askServer, attachment, escalateChatToHuman, headers, mode, runCodingTask, runDesignTask, token]);

  const onAttach = useCallback((files: FileList) => {
    setAttachError(null);
    const file = files.item(0);
    if (!file) return;
    if (file.size > MAX_ATTACH_BYTES) { setAttachError(tr("fileTooLarge")); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result ?? "");
      const base64 = dataUrl.includes(",") ? dataUrl.split(",")[1]! : dataUrl;
      setAttachment({ name: file.name, mimeType: file.type, base64Data: base64 });
    };
    reader.onerror = () => setAttachError(tr("fileReadError"));
    reader.readAsDataURL(file);
  }, [tr]);

  const onCategoryChange = useCallback((id: PermissionCategoryId, requested: PermissionGrantRequestId) => {
    setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, requested } : c)));
  }, []);

  const navSessions: NavSession[] = sessions.map((s) => ({ id: s.id, title: s.title }));
  const preset = COVER_PRESETS.find((p) => p.id === coverPresetId) ?? COVER_PRESETS[0]!;
  const showDesignSurface = mode === "design" || mode === "cover";

  const chatComposer = (
        <ChatComposer
          locale={locale}
          value={input}
          onValueChange={setInput}
          onSubmit={() => void send(input)}
          busy={busy}
          listening={listening}
          onToggleVoice={toggleVoice}
          onAttach={onAttach}
          hasAttachment={attachment !== null}
          attachmentSlot={attachment || attachError || micError || (isPrivileged && voicePanelOpen) ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 8,
                fontSize: 11,
              }}
            >
              {isPrivileged && voicePanelOpen && (
                <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: 10 }}>
                  {!voiceConsent ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ color: C.muted }}>Voice शुरू करने से पहले session consent दें।</span>
                      <button
                        type="button"
                        onClick={() => void enableVoice()}
                        disabled={voiceBusy}
                        style={{
                          border: `1px solid ${C.border}`,
                          borderRadius: 8,
                          padding: "6px 10px",
                          background: C.chip,
                          color: C.fg,
                        }}
                      >
                        {voiceBusy ? "…" : "Voice सक्षम करें"}
                      </button>
                    </div>
                  ) : (
                    <>
                      <ForegroundWakeControl
                        key={`${token ?? "anonymous"}-${voiceConsent ? "consented" : "pending"}`}
                        language={locale === "hi" ? "hi-IN" : "en-IN"}
                        disabled={!voiceConsent}
                        suspended={voiceBusy || voiceRecordingActive}
                        onActiveChange={setForegroundWakeActive}
                        onWakeWord={handleForegroundWakeWord}
                        onWakeCommand={handleForegroundWakeCommandDetected}
                        onStateChange={handleForegroundWakeState}
                      />
                      {voiceBusy && (
                        <button
                          type="button"
                          onClick={cancelVoiceInteraction}
                          style={{
                            alignSelf: "center",
                            border: `1px solid ${C.destructive}`,
                            borderRadius: 8,
                            padding: "6px 10px",
                            background: "transparent",
                            color: C.destructive,
                            cursor: "pointer",
                            fontSize: 12,
                          }}
                        >
                          Voice turn रद्द करें
                        </button>
                      )}
                      <VoiceRecorder
                        disabled={voiceBusy}
                        maxDurationSeconds={30}
                        maxSizeBytes={4 * 1024 * 1024}
                        onRecordingRequested={handleVoiceRecordingRequested}
                        onStateChange={handleVoiceRecordingState}
                        onRecordingComplete={(recording) => void handleVoiceRecording(recording)}
                        onError={(error) => {
                          setMicError(safeErrorText(errorToCode(error), locale).title);
                          setVoiceActivity("error");
                        }}
                      />
                    </>
                  )}
                  {voiceAudioSrc && (
                    <VoicePlayer
                      src={voiceAudioSrc}
                      autoPlay
                      onEnded={() => setVoiceActivity("idle")}
                      onError={() => setVoiceActivity("error")}
                    />
                  )}
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {attachment && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 999, border: `1px solid ${C.border}`, color: C.fg }}>
                    {attachment.name}
                    <button type="button" onClick={() => setAttachment(null)} style={{ border: "none", background: "none", color: C.muted, cursor: "pointer" }}>×</button>
                  </span>
                )}
                {attachError && <span style={{ color: C.destructive }}>{attachError}</span>}
                {micError && <span style={{ color: C.destructive }}>{micError}</span>}
              </div>
            </div>
          ) : undefined}
          selectorSlot={
            <ModeSelector
              locale={locale}
              mode={mode}
              onModeChange={setMode}
              logicalProfile={logicalProfile}
              onLogicalProfileChange={setLogicalProfile}
              effort={effort}
              onEffortChange={setEffort}
              creativity={creativity}
              onCreativityChange={setCreativity}
              coverPresetId={coverPresetId}
              onCoverPresetChange={setCoverPresetId}
            />
          }
          permissionSlot={approvalPending ? (
            <PermissionStrip
              locale={locale}
              categories={categories}
              onCategoryChange={onCategoryChange}
              serverState={serverPermissionState}
              approvalPending={approvalPending}
            />
          ) : undefined}
          menuItems={[
            { id: "projects", icon: "▣", label: locale === "hi" ? "Repositories / Projects" : "Repositories / Projects" },
            { id: "files", icon: "□", label: locale === "hi" ? "फ़ाइलें" : "Files" },
            { id: "skills", icon: "✦", label: locale === "hi" ? "Skills" : "Skills" },
          ]}
          onMenuItemSelect={(item) => {
            window.location.hash = `#${item}`;
          }}
        />
      
  );

  return (
    <ProductShell
      composerPlacement={messages.length === 0 ? "welcome" : "bottom"}
      sidebar={
        <AppNav
          activeRoute={initialRoute}
          collapsed={!leftPanelOpen}
          sessions={navSessions}
          activeSessionId={activeId}
          onNewChat={() => void newChat()}
          onSelectSession={(id) => setActiveId(id)}
          onCollapsedChange={(collapsed) => setLeftPanelOpen(!collapsed)}
        />
      }
      topbar={
        <div className="ss-product-topbar" style={{ height: 52, flexShrink: 0, display: "flex", alignItems: "center", gap: 10, padding: "0 20px" }}>
          <strong style={{ fontSize: 14, fontWeight: 650 }}>
            {mode === "design"
              ? (locale === "hi" ? "डिज़ाइन" : "Design")
              : mode === "cover"
                ? (locale === "hi" ? "कवर" : "Cover")
                : (locale === "hi" ? "नई चैट" : "New chat")}
          </strong>
          <div style={{ flex: 1 }} />
          <span
            role="status"
            aria-label={coreBadge === "live" ? "Smart Shikka Live" : coreBadge === "connected" ? "Smart Shikka Connected" : "Smart Shikka Offline"}
            title={locale === "hi" ? "Smart Shikka स्थिति (server truth)" : "Smart Shikka status (server truth)"}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: C.muted }}
          >
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: coreBadge === "live" ? C.success : coreBadge === "connected" ? "#e6a23c" : C.destructive }} />
            {locale === "hi"
              ? coreBadge === "live" ? "Smart Shikka लाइव" : coreBadge === "connected" ? "Smart Shikka जुड़ा" : "Smart Shikka ऑफ़लाइन"
              : coreBadge === "live" ? "Smart Shikka Live" : coreBadge === "connected" ? "Smart Shikka Connected" : "Smart Shikka Offline"}
          </span>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: status === "ready" ? C.success : C.destructive }} />
          <span style={{ fontSize: 11, color: C.muted }}>{status === "ready" ? tr("statusReady") : statusKind === "request" ? tr("statusRequestError") : tr("statusError")}</span>
          {isPrivileged && (
            <button
              type="button"
              className={`ss-control-pane-toggle${controlPaneOpen ? " is-active" : ""}`}
              aria-label={controlPaneOpen ? (locale === "hi" ? "दायाँ कंट्रोल पैनल बंद करें" : "Close control panel") : (locale === "hi" ? "दायाँ कंट्रोल पैनल खोलें" : "Open control panel")}
              aria-pressed={controlPaneOpen}
              onClick={() => setControlPaneOpen((open) => !open)}
            >
              <span aria-hidden="true">◧</span>
              <span>{locale === "hi" ? "कार्य" : "Activity"}</span>
            </button>
          )}
          <ProductPreferenceControls
            locale={locale}
            theme={theme}
            onToggleTheme={toggleTheme}
            onToggleLocale={() => setLocale(locale === "hi" ? "en" : "hi")}
          />
        </div>
      }
      canvas={
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, flex: 1, minWidth: 0, width: "100%" }}>
          <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <ChatCanvas
              locale={locale}
              avatarSrc={SHIKKA_LOGO}
              messages={messages}
              busy={busy}
              suggestions={messages.length === 0 ? suggestions : undefined}
              onSuggestionClick={(suggestion) => void send(suggestion)}
              onRetryPrompt={(prompt) => void send(prompt)}
              composerSlot={chatComposer}
              ceoPrivileged={isPrivileged === true}
              modeSurface={showDesignSurface ? (
                <DesignModeSurface
                  locale={locale}
                  title={mode === "cover" ? (locale === "hi" ? preset.label.hi : preset.label.en) : productText("designCreate", locale)}
                  dimensionLabel={mode === "cover" ? `${preset.width}×${preset.height} ${preset.unit}` : "1080×1080 px"}
                  busy={busy}
                  artifact={designArtifact}
                  errorMessage={designError}
                  imageGenerationStatus={imageGenerationStatus}
                />
              ) : undefined}
            />
          </div>
          {supportTurn ? (
            <SupportActionsCard
              busy={supportTurn.busy === true}
              ticketNumber={supportTurn.ticketNumber}
              onSolved={() => setSupportTurn(null)}
              onHumanHelp={() => {
                if (!supportTurn.busy && !supportTurn.ticketNumber) {
                  void escalateChatToHuman(supportTurn);
                }
              }}
              onHelpCenter={() => { window.location.hash = "#help"; }}
            />
          ) : null}
          {approvalCard ? (
            <ApprovalConsumeCard
              locale={locale}
              request={approvalCard.request}
              state={approvalCard.state}
              errorCode={approvalCard.errorCode}
              onApprove={() => void resolveApprovalDecision(true)}
              onDeny={() => void resolveApprovalDecision(false)}
              onExpire={() =>
                setApprovalCard((c) => (c && c.state === "pending" ? { ...c, state: "expired" } : c))
              }
            />
          ) : null}
        </div>
      }
      composer={chatComposer}
      contextPane={
        isPrivileged && token && controlPaneOpen ? (
          <WebControlContextPane
            token={token}
            locale={locale}
            projectId={projectId}
            onClose={() => setControlPaneOpen(false)}
          />
        ) : undefined
      }
    />
  );
}
